'use client';

import { useState } from 'react';
import { BellRing, BookmarkCheck, Coins, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertsRequestError,
  type AlertQuoteInput,
  InsufficientCreditsError,
  useAlertQuote,
} from '@/hooks/use-alerts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useInsufficientCreditsToast } from './use-insufficient-credits-toast';

/**
 * "Gespeichert — möchten Sie auch Updates?" shown right after a free save.
 *
 * Saving (a company bookmark, a search) costs nothing; subscribing to
 * updates costs credits per day. This dialog is the one place that turns a
 * save into a subscription, so it always states the price before the click:
 * the exact increment from the quote endpoint, with a static "from" figure
 * while that loads or when it fails.
 *
 * Presentational about *what* gets subscribed — the caller passes
 * `onSubscribe` (the one-click default) and `onCustomize` (opens the full
 * configuration). A 402 shows the top-up prompt and leaves the item saved.
 */
export function SubscribeAfterSaveDialog({
  open,
  onOpenChange,
  kind,
  quoteInput,
  onSubscribe,
  onCustomize,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: 'company' | 'search';
  /** Prices the one-click default; `null` shows only the fallback text. */
  quoteInput: AlertQuoteInput | null;
  onSubscribe: () => Promise<unknown>;
  onCustomize?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const showInsufficient = useInsufficientCreditsToast();
  const [busy, setBusy] = useState(false);
  const quote = useAlertQuote(open ? quoteInput : null);
  const nf = new Intl.NumberFormat(
    i18n.language?.startsWith('de') ? 'de-DE' : 'en-US',
  );

  const fallback =
    kind === 'company'
      ? t('subscribeAfterSave.priceFallbackCompany')
      : t('subscribeAfterSave.priceFallbackSearch');
  let price: string = fallback;
  if (quote.data) {
    const n = quote.data.increment;
    price =
      n > 0
        ? t('subscribeAfterSave.price', { count: n, n: nf.format(n) })
        : t('subscribeAfterSave.priceCovered');
  }

  const subscribe = async () => {
    setBusy(true);
    try {
      await onSubscribe();
      toast.success(t('subscribeAfterSave.subscribed'), {
        position: 'top-center',
      });
      onOpenChange(false);
    } catch (e) {
      if (e instanceof InsufficientCreditsError) {
        // The item stays saved; only the subscription failed.
        showInsufficient(e.needed);
        onOpenChange(false);
        return;
      }
      const body =
        e instanceof AlertsRequestError
          ? (e.body as { error?: unknown; detail?: unknown } | null)
          : null;
      let message = t('alerts.dialog.errorGeneric');
      if (body?.error === 'email_unverified' || body?.detail === 'email_unverified') {
        message = t('pages.insolvenzticker.emailUnverified');
      } else if (e instanceof AlertsRequestError && e.status === 409) {
        message =
          body?.detail === 'duplicate_feed'
            ? t('alerts.dialog.errorDuplicate')
            : t('alerts.dialog.errorTooMany');
      }
      toast.error(message, { position: 'top-center' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkCheck className="size-4" />
            {t('subscribeAfterSave.title')}
          </DialogTitle>
          <DialogDescription>
            {t('subscribeAfterSave.savedFree')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm font-medium text-foreground">
            <BellRing className="mt-0.5 size-4 shrink-0" />
            {t('subscribeAfterSave.question')}
          </p>
          <p className="text-sm text-muted-foreground">
            {kind === 'company'
              ? t('subscribeAfterSave.whatCompany')
              : t('subscribeAfterSave.whatSearch')}
          </p>
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <Coins className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium text-foreground">{price}</span>
            {quote.isLoading && quoteInput && (
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          {onCustomize && (
            <button
              type="button"
              className="min-h-10 text-sm text-primary underline-offset-4 hover:underline disabled:opacity-50 lg:min-h-0"
              disabled={busy}
              onClick={() => {
                onOpenChange(false);
                onCustomize();
              }}
            >
              {t('subscribeAfterSave.customize')}
            </button>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            {t('subscribeAfterSave.justSave')}
          </Button>
          <Button onClick={() => void subscribe()} disabled={busy}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <BellRing className="size-4" />
            )}
            {t('subscribeAfterSave.subscribe')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
